import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'

import Button from '@ui/Button'
// Simplified, brand-colored logomarks (hand-drawn approximations, not
// copies of the official vector files) — enough detail to be instantly
// recognizable while keeping the SVGs tiny.
const VercelIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
    <path fill="#000" d="M12 2 22 20 2 20Z" />
  </svg>
)
const TypeScriptIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
    <rect width="24" height="24" rx="4" fill="#3178C6" />
    <text x="12" y="17" textAnchor="middle" fontSize="11" fontWeight="700" fill="#fff" fontFamily="Arial, sans-serif">TS</text>
  </svg>
)
const NextIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
    <circle cx="12" cy="12" r="12" fill="#000" />
    <text x="12" y="16.5" textAnchor="middle" fontSize="12" fontWeight="700" fill="#fff" fontFamily="Arial, sans-serif">N</text>
  </svg>
)
const TailwindIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
    <path fill="#38BDF8" d="M12 6c-2.67 0-4.33 1.33-5 4 1-1.33 2.17-1.83 3.5-1.5.76.19 1.31.74 1.91 1.35.98 1 2.12 2.15 4.59 2.15 2.67 0 4.33-1.33 5-4-1 1.33-2.17 1.83-3.5 1.5-.76-.19-1.31-.74-1.91-1.35C15.61 7.15 14.47 6 12 6Z" />
    <path fill="#38BDF8" d="M7 12c-2.67 0-4.33 1.33-5 4 1-1.33 2.17-1.83 3.5-1.5.76.19 1.31.74 1.91 1.35.98 1 2.12 2.15 4.59 2.15 2.67 0 4.33-1.33 5-4-1 1.33-2.17 1.83-3.5 1.5-.76-.19-1.31-.74-1.91-1.35C10.61 13.15 9.47 12 7 12Z" />
  </svg>
)
const PrismaIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
    <path fill="#2D3748" d="M11.3 2.3a1 1 0 0 1 1.4 0l8.5 16.2a1 1 0 0 1-.9 1.5H3.7a1 1 0 0 1-.9-1.5Z" />
  </svg>
)
const FigmaIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
    <path fill="#F24E1E" d="M8 1h4v6H8a3 3 0 1 1 0-6Z" />
    <path fill="#FF7262" d="M12 1h4a3 3 0 1 1 0 6h-4Z" />
    <path fill="#A259FF" d="M12 7h4a3 3 0 1 1-3 3V7Z" />
    <path fill="#1ABCFE" d="M8 10a3 3 0 1 1 4 2.83V13a3 3 0 1 1-4-2.83Z" />
    <path fill="#0ACF83" d="M8 13a3 3 0 1 1 4 2.83V22a3 3 0 1 1-4-6.17Z" />
  </svg>
)
const DotNetIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
    <rect width="24" height="24" rx="4" fill="#512BD4" />
    <text x="12" y="16" textAnchor="middle" fontSize="8" fontWeight="700" fill="#fff" fontFamily="Arial, sans-serif">.NET</text>
  </svg>
)
// These 5 already exist in FontAwesome — no custom SVG needed.
const ReactIcon  = () => <i className="fa-brands fa-react"  style={{ color: '#61DAFB', fontSize: '15px' }} aria-hidden="true" />
const NodeIcon   = () => <i className="fa-brands fa-node-js" style={{ color: '#3C873A', fontSize: '15px' }} aria-hidden="true" />
const DockerIcon = () => <i className="fa-brands fa-docker"  style={{ color: '#2496ED', fontSize: '15px' }} aria-hidden="true" />
const GitIcon    = () => <i className="fa-brands fa-git-alt" style={{ color: '#F05032', fontSize: '15px' }} aria-hidden="true" />
const SqlIcon    = () => <i className="fa-solid fa-database" style={{ color: '#4479A1', fontSize: '14px' }} aria-hidden="true" />

// Orbit tiers match the 3 existing rings exactly (44% / 72% / 100%).
// `angle` is the starting position in degrees, clockwise from 12 o'clock.
// Each tier is evenly spaced around its own circle: inner = opposite
// sides (180° apart), middle = 4-way (90° apart), outer = 6-way (60° apart).
const STACK_BADGES = [
  // Inner — 2 badges, exact opposite sides
  { id: 'figma',      label: 'Government',      Icon: FigmaIcon,      tier: 'inner',  angle: 0   },
  { id: 'react',      label: 'Healthcare',      Icon: ReactIcon,      tier: 'inner',  angle: 180 },

  // Middle — 4 badges, 90° apart
  { id: 'nextjs',     label: 'Startup',    Icon: NextIcon,       tier: 'middle', angle: 0   },
  { id: 'nodejs',     label: 'Education',    Icon: NodeIcon,       tier: 'middle', angle: 120  },
  { id: 'tailwind',   label: 'NGO',   Icon: TailwindIcon,   tier: 'middle', angle: 240 },
  // { id: 'dotnet',     label: '.NET',       Icon: DotNetIcon,     tier: 'middle', angle: 270 },

  // Outer — 6 badges, 60° apart
  { id: 'vercel',     label: 'Consulting',     Icon: VercelIcon,     tier: 'outer',  angle: 0   },
  { id: 'typescript', label: 'eCommerce', Icon: TypeScriptIcon, tier: 'outer',  angle: 72 },
  { id: 'sql',        label: 'Textile',        Icon: SqlIcon,        tier: 'outer',  angle: 144 },
  { id: 'prisma',     label: 'FinTech',     Icon: PrismaIcon,     tier: 'outer',  angle: 216 },
  { id: 'docker',     label: 'Energy',     Icon: DockerIcon,     tier: 'outer',  angle: 288 },
  // { id: 'git',        label: 'Git',        Icon: GitIcon,        tier: 'outer',  angle: 300 },
]

// const TIER_SIZE = { inner: '44%', middle: '72%', outer: '100%' }
const TIER_SIZE = {
  inner: '48%',
  middle: '76%',
  outer: '100%',
}
// Inner orbits slowest, outer fastest — matches how real orbital motion looks.
const TIER_DURATION = { inner: 44, middle: 26, outer: 16 } // seconds




export default function Industries() {
   
  return (
   

        <div className="stack-orbit" data-aos="fade-left">
          <span className="stack-ring stack-ring--1" aria-hidden="true" />
          <span className="stack-ring stack-ring--2" aria-hidden="true" />
          <span className="stack-ring stack-ring--3" aria-hidden="true" />
          <span className="stack-center-pill">Industries</span>
          {STACK_BADGES.map((badge) => (
            <span
              key={badge.id}
              className="stack-orbit-track"
              style={{
                width: TIER_SIZE[badge.tier],
                height: TIER_SIZE[badge.tier],
                '--start-angle': `${badge.angle}deg`,
                animationDuration: `${TIER_DURATION[badge.tier]}s`,
              }}
            >
              <span className="stack-badge" style={{ animationDuration: `${TIER_DURATION[badge.tier]}s` }}>
                <badge.Icon /> {badge.label}
              </span>
            </span>
          ))}
        </div>
     
    
  )
}